import { PermissionSetModel } from "client/Api/common";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	createSpace,
	CreateSpaceResult,
	EditSpaceResult,
	getCreateSpaceViewModel,
	getEditSpaceViewModel,
	saveSpace,
	SaveSpaceSettingsModel,
	SpaceKind
} from "client/Api/Workspace/workspace";
import {
	Dialog,
	Form,
	Tabs,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useIsTouchDevice,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation,
	useValueEqualMemo
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { configureValidationStep } from "client/Validation/steps";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { General, GeneralHandle } from "./General";
import { Owners } from "./Owners";
import { WhoHasAccess } from "./WhoHasAccess";
import { SpaceUserInfo } from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type SavedSpace =
	| {
		id: number;
		kind: SpaceKind.Personal;
		userInfo: SpaceUserInfo;
	}
	| {
		id: number;
		kind: SpaceKind.Shared;
		name: string;
		userInfo: SpaceUserInfo;
	};

export type EditSpaceDialogMode =
	| {
		kind: "create";
		templateId?: number;
	}
	| {
		kind: "edit";
		id: number;
	};

export type EditSpaceDialogProps = {
	mode: EditSpaceDialogMode;
	onSaved: (space: SavedSpace) => void;
	onClose: () => void;
	onBack?: () => void;
};

export const EditSpaceDialog = (props: EditSpaceDialogProps): ReactElement => {
	const {
		mode,
		onSaved,
		onClose: onCloseFromProps,
		onBack
	} = props;

	const [tabId, setTabId] = useState<TabId>("who-has-access");
	const [isBusy, setIsBusy] = useState<boolean>(true);

	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>({
		users: EMPTY_ARRAY,
		userGroups: EMPTY_ARRAY
	});

	const [formData, setFormData] = useState<SaveSpaceFormData | undefined>();

	const stableMode = useValueEqualMemo(
		(): EditSpaceDialogMode => mode,
		[mode]
	);

	const generalRef: RefObject<GeneralHandle | null> = useRef<GeneralHandle>(null);

	const isTouchDevice: boolean = useIsTouchDevice();
	const alertManager: AlertManager = useAlerts();
	const onClose: (() => void) = useStableImperativeCallback(onCloseFromProps);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isTouchDeviceRef: ReadonlyRefObject<boolean> = useSynchronizedRef(isTouchDevice);

	const formHandler: IFormHandler<SaveSpaceFormData | undefined> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);

					let formData: SaveSpaceFormData;

					switch (stableMode.kind) {
						case "create":
							const result = await getCreateSpaceViewModel(signal);
							setUsersAndUserGroupsData(result.usersAndUserGroupsData);
							const { userId } = userConfiguration()
								.currentUserIdentity;

							formData = {
								kind: SpaceKind.Shared,
								originalName: null,
								name: "",
								whoHasAccess: {
									everyone: true,
									specificSet: {
										userIds: EMPTY_ARRAY,
										userGroupIds: EMPTY_ARRAY
									}
								},
								owners: {
									userIds: [userId],
									userGroupIds: EMPTY_ARRAY
								}
							};

							break;
						case "edit":
							const { model, usersAndUserGroupsData } = await getEditSpaceViewModel(stableMode.id, signal);
							setUsersAndUserGroupsData(usersAndUserGroupsData);

							switch (model.spaceKind) {
								case SpaceKind.Personal:
									formData = {
										kind: SpaceKind.Personal,
										whoHasAccess: model.whoHasAccess
									};
									break;
								case SpaceKind.Shared:
									formData = {
										kind: SpaceKind.Shared,
										originalName: model.name,
										name: model.name,
										whoHasAccess: model.whoHasAccess,
										owners: model.owners
									};
									break;
								default:
									never(model);
							}

							break;
						default:
							never(stableMode);
					}

					setFormData(formData);

					const activeTabId: TabId = formData.kind === SpaceKind.Shared
						? "general"
						: "who-has-access";

					setTabId(activeTabId);

					if (!isTouchDeviceRef.current && activeTabId === "general") {
						// The general tab content is most likely not rendered yet at this point
						setTimeout(
							(): void => generalRef.current
								?.focusFirstInput(),
							1
						);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			stableMode,
			isTouchDeviceRef,
			onClose
		]
	);

	const dialogTitle = useMemo(
		(): string => getDialogTitle(stableMode, formData),
		[formData, stableMode]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const ensuredFormData: SaveSpaceFormData = ensureNotNil(formData);

				const model: SaveSpaceSettingsModel = prepareForSaving(ensuredFormData);

				let persistedId: number;
				let userInfo: SpaceUserInfo;

				switch (stableMode.kind) {
					case "create":
						const creationResult: CreateSpaceResult = await createSpace(
							model,
							stableMode.templateId,
							globalCancellation
						);

						persistedId = creationResult.id;
						userInfo = creationResult.userInfo;
						break;
					case "edit":
						persistedId = stableMode.id;
						const editResult: EditSpaceResult = await saveSpace(
							stableMode.id,
							model,
							globalCancellation
						);

						userInfo = editResult.userInfo;
						break;
					default:
						never(stableMode);
				}

				onSaved(
					convertForFinalization(
						persistedId,
						ensuredFormData,
						userInfo
					)
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			stableMode,
			isBusy,
			onSaved
		]
	);

	return (
		<Dialog
			trackingId="edit-space-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={dialogTitle}
				onClose={onClose}
				onBack={onBack}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler} height="fill">
					<Tabs<TabId>
						activeTabId={tabId}
						onActiveTabIdChange={setTabId}
						height="fill"
					>
						{formHandler.whenType(
							(x) => x?.kind === SpaceKind.Shared,
							(attachable) => (
								<Tabs.Tab<TabId>
									{...formHandler.forStep(generalTabValidationStep).attachToTab()}
									id="general"
									title={localize("_General")}
								>
									<General
										attachable={attachable}
										ref={generalRef}
									/>
								</Tabs.Tab>
							)
						)}

						{formHandler.whenType(
							isNotNil,
							(attachable) => (
								<Tabs.Tab<TabId>
									{...formHandler.forStep(whoHasAccessTabValidationStep).attachToTab()}
									id="who-has-access"
									title={localize("_WhoHasAccess")}
								>
									<WhoHasAccess
										attachable={attachable.for("whoHasAccess")}
										usersAndUserGroupsData={usersAndUserGroupsData}
									/>
								</Tabs.Tab>
							)
						)}

						{formHandler.whenType(
							(x) => x?.kind === SpaceKind.Shared,
							(attachable) => (
								<Tabs.Tab<TabId>
									{...formHandler.forStep(ownersTabValidationStep).attachToTab()}
									id="owners"
									title={localize("_Owners")}
								>
									<Owners
										attachable={attachable.for("owners")}
										usersAndUserGroupsData={usersAndUserGroupsData}
									/>
								</Tabs.Tab>
							)
						)}
					</Tabs>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EditSpaceDialog.displayName = "EditSpaceDialog";

type TabId =
	| "general"
	| "who-has-access"
	| "owners";

export type EditSpaceDialogInitialData = Omit<
	EditSpaceDialogProps,
	"onSaved" | "onClose"
>;

export function useEditSpaceDialog(): [
	element: ReactElement | null,
	open: (props: EditSpaceDialogInitialData) => Promise<TryResult<SavedSpace>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditSpaceDialog
				{...props}
				onSaved={onSave}
				onClose={onClose}
			/>
		)
	);
}

const validationConfiguration = createValidationConfiguration<SaveSpaceFormData | undefined>();

validationConfiguration.add(isNotNullNorUndefined());

const setValidationConfiguration = validationConfiguration.whenType(isNotNil);

setValidationConfiguration.forObject("whoHasAccess")
	.add(
		isNotNullNorUndefined()
	);

const sharedSpaceValidationConfiguration = setValidationConfiguration
	.whenType((x) => x.kind === SpaceKind.Shared);

sharedSpaceValidationConfiguration
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(32)
	);

sharedSpaceValidationConfiguration
	.forObject("owners")
	.add(
		isNotNullNorUndefined()
	);

const generalTabValidationStep = configureValidationStep<SaveSpaceFormData | undefined>()
	.whenType(isNotNil)
	.whenType((x) => x.kind === SpaceKind.Shared)
	.add("name");

const whoHasAccessTabValidationStep = configureValidationStep<SaveSpaceFormData | undefined>()
	.whenType(isNotNil)
	.add("whoHasAccess");

const ownersTabValidationStep = configureValidationStep<SaveSpaceFormData | undefined>()
	.whenType(isNotNil)
	.whenType((x) => x.kind === SpaceKind.Shared)
	.add("owners");

function getDialogTitle(mode: EditSpaceDialogMode, formData: SaveSpaceFormData | undefined): string {
	switch (mode.kind) {
		case "create":
			return localize("_NewSharedSpace");
		case "edit":
			if (isNil(formData)) {
				return "";
			} else {
				return getDialogTitleForEditMode(formData);
			}
		default:
			never(mode);
	}
}

function getDialogTitleForEditMode(formData: SaveSpaceFormData): string {
	switch (formData.kind) {
		case SpaceKind.Personal:
			return localize("_EditPersonalSpace");
		case SpaceKind.Shared:
			return resolveFormattedTextString({
				template: localize("_EditNameSpace"),
				replacements: [{
					what: "{{Name}}",
					filler: formData.originalName ?? ""
				}]
			});
		default:
			never(formData);
	}
}

export type PersonalSpaceFormData = {
	kind: SpaceKind.Personal;
} & Pick<SaveSpaceSettingsModel, "whoHasAccess">;

export type SharedSpaceFormData = {
	kind: SpaceKind.Shared;
	originalName: string | null;
} & OverrideProperties<
	SaveSpaceSettingsModel,
	{
		name: string;
		owners: PermissionSetModel;
	}
>;

export type SaveSpaceFormData = PersonalSpaceFormData | SharedSpaceFormData;

function prepareForSaving(formData: SaveSpaceFormData): SaveSpaceSettingsModel {
	let { whoHasAccess } = formData;

	if (whoHasAccess.everyone) {
		whoHasAccess = {
			everyone: true,
			specificSet: null
		};
	} else {
		whoHasAccess = {
			...whoHasAccess,
			specificSet: whoHasAccess.specificSet ?? {
				userIds: EMPTY_ARRAY,
				userGroupIds: EMPTY_ARRAY
			}
		};
	}

	switch (formData.kind) {
		case SpaceKind.Personal:
			return {
				whoHasAccess,
				name: null,
				owners: null
			};
		case SpaceKind.Shared:
			return {
				name: formData.name,
				whoHasAccess,
				owners: formData.owners
			};
		default:
			never(formData);
	}
}

function convertForFinalization(
	id: number,
	formData: SaveSpaceFormData,
	userInfo: SpaceUserInfo
): SavedSpace {
	switch (formData.kind) {
		case SpaceKind.Personal:
			return {
				id,
				kind: SpaceKind.Personal,
				userInfo
			};
		case SpaceKind.Shared:
			return {
				id,
				kind: SpaceKind.Shared,
				name: formData.name,
				userInfo
			};
		default:
			never(formData);
	}
}
